import {GetTireRecommendedResponse} from '@di-shared/api/di/__generated_eu/models/GetTireRecommendedResponse';
import {TireRecommendedData} from '@di-shared/api/di/__generated_eu/models/TireRecommendedData';
import type {TranslationKey} from '@di-shared/i18next';
import {RecommendedTireData, TirePressureUnit} from './types';

/**
 * 공기압 단위를 문자열로 변환
 */
export function getPressureUnitString(unit: TirePressureUnit): string {
  switch (unit) {
    case TirePressureUnit.PSI: // TirePressureUnit.PSI
      return 'psi';
    case TirePressureUnit.KPA: // TirePressureUnit.KPA
      return 'kPa';
    case TirePressureUnit.BAR: // TirePressureUnit.BAR
      return 'bar';
    default:
      return 'psi';
  }
}

/**
 * single spec 판단: 모든 타이어의 권장 공기압이 동일한지 확인
 */
export function isSingleSpec(tirePsiList: Array<TireRecommendedData>): boolean {
  if (tirePsiList.length === 0) return true;
  if (tirePsiList.length === 1) return true;

  const firstFrontPsi = tirePsiList[0].frontPsi;
  const firstRearPsi = tirePsiList[0].rearPsi;

  return tirePsiList.every((item) => item.frontPsi === firstFrontPsi && item.rearPsi === firstRearPsi);
}

/**
 * 등록된 타이어 정보 찾기
 */
export function findRegisteredTireData(
  tirePsiList: Array<TireRecommendedData>,
  registeredFrontTire?: string,
  registeredRearTire?: string
): TireRecommendedData | null {
  if (!registeredFrontTire && !registeredRearTire) {
    return null;
  }

  // 등록된 앞/뒤 타이어와 일치하는 항목 찾기
  return tirePsiList.find((item) => item.frontTire === registeredFrontTire && item.rearTire === registeredRearTire) || null;
}

/**
 * 타이어 이름 표시 형식 생성
 */
export function getTireDisplayName(registeredFrontTire?: string, registeredRearTire?: string, t?: (key: TranslationKey) => string): string {
  if (!registeredFrontTire && !registeredRearTire) return '';
  if (registeredFrontTire === registeredRearTire) return registeredFrontTire || '';

  // fallback: 영어
  const frontLabel = t ? t('ctn_tire_Recommended_Pressure_selected_front_eu') : 'Front';
  const rearLabel = t ? t('ctn_tire_Recommended_Pressure_selected_rear_eu') : 'Rear';

  return `${registeredFrontTire || ''}(${frontLabel}) ${registeredRearTire || ''}(${rearLabel})`;
}

/**
 * API 응답을 UI RecommendedTireData 타입으로 변환
 */
export function convertToRecommendedTireData(
  data: GetTireRecommendedResponse,
  unit: TirePressureUnit,
  t?: (key: any) => string
): RecommendedTireData | null {
  // 등록된 타이어가 있으면 해당 타이어 데이터 사용
  let tireData = findRegisteredTireData(data.tirePsiList, data.registeredFrontTire, data.registeredRearTire);
  let tireName = getTireDisplayName(data.registeredFrontTire, data.registeredRearTire, t);

  // 등록된 타이어가 없지만 single spec이면 첫 번째 타이어 데이터 사용
  if (!tireData && isSingleSpec(data.tirePsiList) && data.tirePsiList.length > 0) {
    tireData = data.tirePsiList[0];
    tireName = getTireDisplayName(tireData.frontTire, tireData.rearTire, t);
  }

  if (!tireData) {
    return null;
  }

  const unitString = getPressureUnitString(unit);

  return {
    tireName,
    frontPressure: {
      pressure: unit === TirePressureUnit.PSI ? tireData.frontPsi : unit === TirePressureUnit.KPA ? tireData.frontKpa : tireData.frontBar,
      unit: unitString,
    },
    rearPressure: {
      pressure: unit === TirePressureUnit.PSI ? tireData.rearPsi : unit === TirePressureUnit.KPA ? tireData.rearKpa : tireData.rearBar,
      unit: unitString,
    },
  };
}
